import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, CardHeader, CardMedia, Dialog, Divider, Grid, IconButton, Typography, useTheme } from '@mui/material';
import React from 'react';
import styled from 'styled-components';
import { useAuthContext } from '../../../context/AuthContextProvider';
import { Post } from '../../../models/Post';
import { getAvatar } from '../../../models/User';
import { formatDateWithBars, hexToRgba } from '../../../utils/utils';

const ImageContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  height: '100vh',
  width: '100%',
  backgroundColor: 'black'
});

const ImageWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  height: '100%',
  maxHeight: '100vh',
  width: '100%',
  overflow: 'hidden'
});

const ButtonContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0
});

const ButtonLeft = styled(IconButton)({
  position: 'absolute',
  left: 0
});

const ButtonRight = styled(IconButton)({
  position: 'absolute',
  right: 0
});

const TextContainer = styled(Box)({
  maxHeight: 'calc(100vh - 200px)',
  overflowY: 'auto',
  padding: '16px'
});

interface PostDialogProps {
  post: Post;
  onClose: () => void;
}

export const PostDetailsDialog = ({ post, onClose }: PostDialogProps) => {
  const theme = useTheme();
  const { user } = useAuthContext();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  const handleNextImage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % post.arquivos.length);
  };

  const handlePrevImage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + post?.arquivos?.length) % post?.arquivos?.length);
  };

  return (
    <Dialog open={true} onClose={onClose} fullScreen>
      <Grid container>
        <Grid item xs={12} sm={post.arquivos.length > 0 ? 3 : 12}>
          <Box textAlign="left" padding={2}>
            <CardHeader
              avatar={<Avatar aria-label="user avatar">{getAvatar(user.nome || '')}</Avatar>}
              title={post.criado_por.nome}
              subheader={formatDateWithBars(post.criado_em)}
              titleTypographyProps={{ variant: 'h6' }}
            />
            <TextContainer>
              <Typography variant="body1" gutterBottom>
                <p style={{ whiteSpace: 'pre-line' }}>{post?.descricao}</p>
              </Typography>
            </TextContainer>
            <Divider />
          </Box>
        </Grid>
        {post.arquivos.length > 0 && (
          <Grid item xs={12} sm={9}>
            <ImageContainer>
              <ImageWrapper>
                {post.arquivos[currentImageIndex].tipo === 'application/pdf' ? (
                  <iframe
                    src={`data:${post.arquivos[currentImageIndex].tipo};base64,${post?.arquivos?.[currentImageIndex]?.base64}`}
                    width="100%"
                    height="100%"
                    title="post file"
                    style={{
                      objectFit: 'contain',
                      maxHeight: '100vh', // Limitar a altura máxima do PDF
                      backgroundColor: 'white'
                    }}
                  />
                ) : post.arquivos[currentImageIndex].tipo.startsWith('video/') ? (
                  <video
                    controls
                    width="100%"
                    height="100%"
                    style={{
                      objectFit: 'contain',
                      maxHeight: '100vh', // Limitar a altura máxima do vídeo
                      backgroundColor: 'black'
                    }}
                    src={`data:${post.arquivos[currentImageIndex].tipo};base64,${post?.arquivos?.[currentImageIndex]?.base64}`}
                  >
                    <track kind="captions" />
                  </video>
                ) : (
                  <CardMedia
                    component="img"
                    width="100%"
                    height="100%"
                    image={`data:${post.arquivos[currentImageIndex].tipo};base64,${post?.arquivos?.[currentImageIndex]?.base64}`}
                    alt="post image"
                    sx={{
                      objectFit: 'contain',
                      maxHeight: '100vh', // Limitar a altura máxima da imagem
                      p: '2rem',
                      backgroundColor: 'black'
                    }}
                  />
                )}
              </ImageWrapper>
              {post?.arquivos?.length > 1 && (
                <ButtonContainer>
                  <ButtonLeft
                    aria-label="previous image"
                    onClick={handlePrevImage}
                    disabled={currentImageIndex === 0}
                    sx={{
                      backgroundColor: 'white',
                      color: 'black',
                      borderRadius: '50%',
                      '&:hover': {
                        backgroundColor: 'black',
                        color: 'white'
                      }
                    }}
                  >
                    <LeftOutlined />
                  </ButtonLeft>
                  <ButtonRight
                    aria-label="next image"
                    onClick={handleNextImage}
                    disabled={currentImageIndex === post?.arquivos?.length - 1}
                    sx={{
                      backgroundColor: 'white',
                      color: 'black',
                      opacity: 0.8,
                      borderRadius: '50%',
                      '&:hover': {
                        backgroundColor: 'black',
                        color: 'white'
                      }
                    }}
                  >
                    <RightOutlined />
                  </ButtonRight>
                </ButtonContainer>
              )}
            </ImageContainer>
          </Grid>
        )}
      </Grid>
      <IconButton
        sx={{
          position: 'absolute',
          top: 30,
          right: 30,
          color: 'white',
          backgroundColor: hexToRgba(theme.palette.primary.main, 0.3),
          '&:hover': {
            backgroundColor: hexToRgba(theme.palette.primary.main, 1)
          }
        }}
        onClick={onClose}
      >
        <CloseIcon fontSize="large" />
      </IconButton>
    </Dialog>
  );
};
